import React, { Dispatch, forwardRef, SetStateAction, useImperativeHandle } from 'react';
import moment from 'moment';

import { EDateInput, EFormSection, EMultiSelect, ESelect, ETextInput } from 'libs/ui';

import useFlightCommission from '../Hooks/useFlightCommission';


interface IProps {
    // isSaving: boolean;
    setIsSaving: Dispatch<SetStateAction<boolean>>;
    selectedService: string | null;
};

// Define the type of the ref's methods
export interface ChildRef {
    save: () => void;
};


const FlightCommission = forwardRef<ChildRef, IProps>(
    ({ setIsSaving, selectedService }, ref) => {

        const {
            searchTypeDropdown,
            travelTypeDropdown,
            commissionFormChargesDropdown,
            groupTypes,
            flightAllClassTypes,
            flightCabinClassTypes,
            supplierList,
            airlinesList,
            getAirlinesBySuppliers,
            allCountryList,
            airportByCoutries,
            getAllAirportByCountries,
            airportByDestinationCoutries,
            getAllAirportByDestinationCountries,
            commissionDetails,
            setCommissionDetails,
            handleSubmit,
            updateCommissionFormCharge,
        } = useFlightCommission({
            setIsSaving,
            selectedService
        });


        useImperativeHandle(ref, () => ({
            save: () => {
                handleSubmit();
            },
        }));


        return (
            <div>
                <EFormSection
                    fullWidth
                    title="Commission Details"
                    subtitle="Fill the mandatory (*) details"
                >
                    <div className="grid grid-cols-3 gap-6">
                        <EMultiSelect
                            label="Group"
                            placeholder="Select"
                            data={groupTypes}
                            required={commissionDetails.rules.affiliateGroupIds === "YES"}
                            error={commissionDetails.errors.affiliateGroupIds}
                            value={commissionDetails.details.affiliateGroupIds.map((item) => String(item))}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        affiliateGroupIds: value.map((item) => Number(item))
                                    }
                                })
                            }}
                        />
                        <EMultiSelect
                            label="Flight Types"
                            placeholder="Select"
                            data={travelTypeDropdown.map(each => ({
                                label: each.key,
                                value: String(each.value)
                            }))}
                            required={commissionDetails.rules.flightTypes === "YES"}
                            error={commissionDetails.errors.flightTypes}
                            value={commissionDetails.details.flightTypes ?? undefined}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        flightTypes: value
                                    }
                                })
                            }}
                        />
                        <EMultiSelect
                            label="Search Type"
                            placeholder="Select"
                            data={searchTypeDropdown.map(each => ({
                                label: each.key,
                                value: String(each.value)
                            }))}
                            required={commissionDetails.rules.flightSearchTypes === "YES"}
                            error={commissionDetails.errors.flightSearchTypes}
                            value={commissionDetails.details.flightSearchTypes}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        flightSearchTypes: value
                                    }
                                })
                            }}
                        />
                        <EMultiSelect
                            label="Flight Class"
                            placeholder="Select"
                            data={flightAllClassTypes}
                            required={commissionDetails.rules.flightClass === "YES"}
                            error={commissionDetails.errors.flightClass}
                            value={commissionDetails.details.flightClass}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        flightClass: value
                                    }
                                })
                            }}
                        />
                        <EMultiSelect
                            label="Flight Cabin Class"
                            placeholder="Select"
                            data={flightCabinClassTypes}
                            required={commissionDetails.rules.flightCabin === "YES"}
                            error={commissionDetails.errors.flightCabin}
                            value={commissionDetails.details.flightCabin}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        flightCabin: value
                                    }
                                })
                            }}
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-6">
                        <EDateInput
                            label="Start Date"
                            placeholder="Start Date"
                            minDate={new Date()}
                            required={commissionDetails.rules.startDate === "YES"}
                            error={commissionDetails.errors.startDate}
                            value={commissionDetails.details.startDate ? new Date(commissionDetails.details.startDate) : undefined}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        startDate: value ? moment(value).format("YYYY-MM-DD") : null
                                    }
                                })
                            }}
                        />
                        <EDateInput
                            size="md"
                            disabled={!commissionDetails.details.startDate}
                            minDate={commissionDetails.details.startDate ? new Date(commissionDetails.details.startDate) : undefined}
                            label="End Date"
                            placeholder="End Date"
                            required={commissionDetails.rules.endDate === "YES"}
                            error={commissionDetails.errors.endDate}
                            value={commissionDetails.details.endDate ? new Date(commissionDetails.details.endDate) : undefined}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        endDate: value ? moment(value).format("YYYY-MM-DD") : null
                                    }
                                })
                            }}
                        />
                        <div></div>
                    </div>

                    <div className="grid grid-cols-3 gap-6">
                        <EMultiSelect
                            label="Suppliers"
                            placeholder="Select"
                            data={supplierList}
                            required={commissionDetails.rules.flightTicketSupplierIds === "YES"}
                            error={commissionDetails.errors.flightTicketSupplierIds}
                            value={commissionDetails.details.flightTicketSupplierIds.map((item) => String(item))}
                            onChange={(value) => {
                                getAirlinesBySuppliers(value);
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        flightTicketSupplierIds: value.map((item) => Number(item))
                                    }
                                })
                            }}
                        />
                        <EMultiSelect
                            label="Airlines "
                            placeholder="Select"
                            data={airlinesList}
                            required={commissionDetails.rules.flightAirlineIds === "YES"}
                            error={commissionDetails.errors.flightAirlineIds}
                            value={commissionDetails.details.flightAirlineIds.map((item) => String(item))}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        flightAirlineIds: value.map((item) => Number(item))
                                    }
                                })
                            }}
                        />
                        <div></div>
                    </div>

                    <div className="grid grid-cols-3 gap-6">
                        <EMultiSelect
                            label="Origin Country"
                            placeholder="Select"
                            data={allCountryList}
                            required={commissionDetails.rules.flightOriginCountryIds === "YES"}
                            error={commissionDetails.errors.flightOriginCountryIds}
                            value={commissionDetails.details.flightOriginCountryIds.map((item) => String(item))}
                            onChange={(value) => {
                                getAllAirportByCountries(value);
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        flightOriginCountryIds: value.map((item) => Number(item))
                                    }
                                })
                            }}
                        />
                        <EMultiSelect
                            label="Destination Country"
                            placeholder="Select"
                            data={allCountryList}
                            required={commissionDetails.rules.flightDestinationCountryIds === "YES"}
                            error={commissionDetails.errors.flightDestinationCountryIds}
                            value={commissionDetails.details.flightDestinationCountryIds.map((item) => String(item))}
                            onChange={(value) => {
                                getAllAirportByDestinationCountries(value);
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        flightDestinationCountryIds: value.map((item) => Number(item))
                                    }
                                })
                            }}
                        />
                        <div></div>
                    </div>

                    <div className="grid grid-cols-3 gap-6">
                        <EMultiSelect
                            label="Route Origin"
                            placeholder="Select"
                            data={airportByCoutries}
                            required={commissionDetails.rules.originAirportIds === "YES"}
                            error={commissionDetails.errors.originAirportIds}
                            value={commissionDetails.details.originAirportIds.map((item) => String(item))}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        originAirportIds: value.map((item) => Number(item))
                                    }
                                })
                            }}
                        />
                        <EMultiSelect
                            label="Route Destination"
                            placeholder="Select"
                            data={airportByDestinationCoutries}
                            required={commissionDetails.rules.destinationAirportIds === "YES"}
                            error={commissionDetails.errors.destinationAirportIds}
                            value={commissionDetails.details.destinationAirportIds.map((item) => String(item))}
                            onChange={(value) => {
                                setCommissionDetails({
                                    ...commissionDetails,
                                    details: {
                                        ...commissionDetails.details,
                                        destinationAirportIds: value.map((item) => Number(item))
                                    }
                                })
                            }}
                        />
                        <div></div>
                    </div>
                </EFormSection>

                {/* COMMISSION CHARGES */}
                <EFormSection
                    fullWidth
                    title="Commission Charges"
                    subtitle="Fill the charge rules"
                >
                    <>
                        {
                            commissionFormChargesDropdown.map(
                                (each, index) => !["OTHER_TAXES", "INCENTIVE", "CASHBACK"].includes(String(each.value))
                                    ? (
                                        <div
                                            key={index + "_com_charge"}
                                            className="grid grid-cols-3 gap-6"
                                        >
                                            <ETextInput
                                                type="number"
                                                label={each.key}
                                                placeholder="Enter charge amount"
                                                value={commissionDetails.details.commissionChargeWithAmountTypes.find((item) => item.commissionFormCharge === each.value)?.amount}
                                                onChange={e => {
                                                    console.log("e.target.value: ", +e.target.value);
                                                    updateCommissionFormCharge(each.value as string, "CHARGE", e.target.value);
                                                }}
                                            />
                                            <ESelect
                                                label="Charge Type"
                                                placeholder="Select"
                                                data={[
                                                    { label: "Percentage", value: "PERCENTAGE" },
                                                    // { label: "Fixed", value: "FIXED" },
                                                ]}
                                                value={commissionDetails.details.commissionChargeWithAmountTypes.find((item) => item.commissionFormCharge === each.value)?.feeType}
                                                onChange={value => updateCommissionFormCharge(each.value as string, "FEE_TYPE", value as "PERCENTAGE" | "FIXED")}
                                            />
                                        </div>
                                    ) : <></>
                            )
                        }
                    </>
                </EFormSection>
            </div>
        );

    });

export default FlightCommission;